import isNil from 'lodash.isnil'
import { observer } from 'mobx-react-lite'

import {
  DateFilterDirectionNamesByDirection,
  getLabelForDateFilterDelayType,
  getAbbreviationForNumberFilterComparison,
} from 'src/components/TableViews/util'
import { useMyOrg } from 'src/layouts/AppStateContextLayout/utils'
import { TableViewDateFilter } from 'src/models/TableViews/TableViewDateFilter'
import { TableViewFilter } from 'src/models/TableViews/TableViewFilter'
import { filterNilOrEmptyString } from 'src/utils'
import { DateFormats, formatDate } from 'src/utils/date'
import { formatNumber } from 'src/utils/number'

type Props = {
  filter: TableViewFilter
}
const FilterTagValue = ({ filter }: Props) => {
  const org = useMyOrg()
  const paymentProviderFormatted = org?.paymentProviderFormatted

  if (!isNil(filter.bool)) {
    return filter.bool ? 'True' : 'False'
  }
  if (TableViewDateFilter.isDatePopulated(filter.date)) {
    const formatFn = (date: string) => {
      const dateFormat = DateFormats.MonthNameDateNoTime
      return formatDate(date, dateFormat)
    }
    switch (filter.date.type) {
      case 'Exact': {
        const start = filter.date.exactStart
        const end = filter.date.exactEnd
        if (end && !start) {
          return `On or before ${formatFn(end)}`
        }
        if (start && !end) {
          return `On or after ${formatFn(start)}`
        }
        if (!start || !end) {
          return '-' // Should not happen
        }
        return `${formatFn(start)} - ${formatFn(end)}`
      }
      case 'IsNotNull':
        return 'Is not empty'
      case 'IsNull':
        return 'Is empty'
      case 'RelativeToToday':
        if (
          !filter.date.relativeToTodayDirection ||
          !filter.date.relativeToTodayDelayType ||
          isNil(filter.date.relativeToTodayCount)
        ) {
          return '-' // Should not happen
        }
        return `${
          DateFilterDirectionNamesByDirection[filter.date.relativeToTodayDirection]
        } ${filter.date.relativeToTodayCount} ${getLabelForDateFilterDelayType(
          filter.date.relativeToTodayDelayType,
          filter.date.relativeToTodayCount > 1
        )}`
      case 'Today':
        return 'Today'
      case 'Tomorrow':
        return 'Tomorrow'
      case 'Yesterday':
        return 'Yesterday'
      case 'ThisWeek':
        return 'This week'
      case 'ThisMonth':
        return 'This month'
      case 'ThisYear':
        return 'This year'
    }
    return '-'
  }
  if (!isNil(filter.isNull)) {
    return filter.isNull ? 'Is empty' : 'Is not empty'
  }

  if (!isNil(filter.number) && !isNil(filter.numberComparison)) {
    const format = filter.numberFormat
    return `${getAbbreviationForNumberFilterComparison(filter.numberComparison)} ${formatNumber(
      filter.number,
      { format }
    )}`
  }

  if (!isNil(filter.optionsSelected)) {
    const filterName = filter.membershipType === 'IsNotIn' ? 'Is not' : ''
    return `${filterName ? `${filterName} (` : ''}${filterNilOrEmptyString(
      filter.optionsSelected.map((selectedOption) =>
        filter.getOptionDisplayValue(selectedOption, paymentProviderFormatted ?? '')
      )
    )
      .sort()
      .join(', ')}${filterName ? ')' : ''}`
  }
}

export default observer(FilterTagValue)
